<template>
    <UserLayout :page-title="t('supportTickets')" :page-section="t('helpCenter')">
        <div class="sti-wrap">

            <!-- Welcome Strip -->
            <div class="sti-welcome anim-up" style="--d:0ms">
                <div class="sti-welcome-left">
                    <div class="sti-welcome-icon">
                        <svg width="22" height="22" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/>
                        </svg>
                    </div>
                    <div>
                        <div class="sti-welcome-eyebrow">{{ t('helpCenter') }}</div>
                        <div class="sti-welcome-title">{{ t('supportTickets') }}</div>
                    </div>
                </div>
                <div class="sti-welcome-chips">
                    <div class="sti-chip sti-chip--purple">
                        <span class="sti-chip-lbl">{{ t('totalTickets') }}</span>
                        <span class="sti-chip-val">{{ ticketStats.total }}</span>
                    </div>
                    <div class="sti-chip sti-chip--blue">
                        <span class="sti-chip-lbl">{{ t('open') }}</span>
                        <span class="sti-chip-val">{{ ticketStats.open }}</span>
                    </div>
                    <div class="sti-chip sti-chip--green">
                        <span class="sti-chip-lbl">{{ t('resolved') }}</span>
                        <span class="sti-chip-val">{{ ticketStats.resolved }}</span>
                    </div>
                </div>
            </div>

            <!-- Stat Cards -->
            <div class="sti-stats anim-up" style="--d:60ms">
                <div class="sti-stat" style="--ac:167,93,237">
                    <div class="sti-stat-label">{{ t('totalTickets') }}</div>
                    <div class="sti-stat-val">{{ ticketStats.total }}</div>
                    <div class="sti-stat-sub" style="color:#a75ded">{{ t('allTime') }}</div>
                </div>
                <div class="sti-stat" style="--ac:59,130,246">
                    <div class="sti-stat-label">{{ t('open') }}</div>
                    <div class="sti-stat-val">{{ ticketStats.open }}</div>
                    <div class="sti-stat-sub" style="color:#3b82f6">{{ t('active') }}</div>
                </div>
                <div class="sti-stat" style="--ac:251,191,36">
                    <div class="sti-stat-label">{{ t('pending') }}</div>
                    <div class="sti-stat-val">{{ ticketStats.pending }}</div>
                    <div class="sti-stat-sub" style="color:#fbbf24">{{ t('waiting') }}</div>
                </div>
                <div class="sti-stat" style="--ac:34,197,94">
                    <div class="sti-stat-label">{{ t('resolved') }}</div>
                    <div class="sti-stat-val">{{ ticketStats.resolved }}</div>
                    <div class="sti-stat-sub" style="color:#22c55e">{{ t('completed') }}</div>
                </div>
            </div>

            <!-- Filter Card -->
            <div class="sti-card anim-up" style="--d:100ms">

                <div class="sti-filter-row">
                    <div class="sti-filter-search">
                        <label class="sti-field-label">{{ t('searchTickets') }}</label>
                        <input v-model="searchQuery" type="text" :placeholder="t('searchPlaceholder')"
                            class="sti-input" @keyup.enter="applyFilters" />
                    </div>
                    <div class="sti-filter-status">
                        <label class="sti-field-label">{{ t('status') }}</label>
                        <select v-model="selectedStatus" class="sti-input sti-select">
                            <option value="">{{ t('allStatuses') }}</option>
                            <option value="open">{{ t('open') }}</option>
                            <option value="pending">{{ t('pending') }}</option>
                            <option value="resolved">{{ t('resolved') }}</option>
                            <option value="closed">{{ t('closed') }}</option>
                        </select>
                    </div>
                    <div class="sti-filter-actions">
                        <button @click="applyFilters" class="sti-btn-icon" :title="t('applyFilters')">
                            <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                            </svg>
                        </button>
                        <button @click="clearFilters" class="sti-btn-ghost">{{ t('clear') }}</button>
                        <Link href="/user/support-tickets/create" class="sti-btn-primary">
                            <span class="sti-hide-xs">{{ t('createTicket') }}</span>
                            <span class="sti-show-xs">{{ t('create') }}</span>
                        </Link>
                    </div>
                </div>
            </div>

            <!-- Tickets List -->
            <div class="sti-card anim-up" style="--d:140ms">

                <div class="sti-list-head">
                    <div>
                        <div class="sti-list-title">{{ t('yourTickets') }}</div>
                        <div class="sti-list-sub">{{ t('manageSupportRequests') }}</div>
                    </div>
                </div>

                <!-- Ticket items -->
                <div v-if="tickets.length" class="sti-list">
                    <div v-for="(ticket, index) in tickets" :key="ticket.id"
                        class="sti-ticket-item"
                        tabindex="0"
                        :aria-label="`${t('ticket')} ${index + 1}: ${ticket.subject}`"
                        @click="router.visit(`/user/support-tickets/${ticket.id}`)"
                        @keyup.enter="router.visit(`/user/support-tickets/${ticket.id}`)">

                        <div class="sti-ticket-icon" :style="`background:${getStatusBgColor(ticket.status)}`">
                            <svg width="20" height="20" :style="`color:${getStatusColor(ticket.status)}`" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" :d="getStatusIcon(ticket.status)"/>
                            </svg>
                        </div>

                        <div class="sti-ticket-body">
                            <div class="sti-ticket-top">
                                <span class="sti-ticket-subject">{{ ticket.subject }}</span>
                                <div class="sti-ticket-badges">
                                    <span class="sti-badge"
                                        :style="`background:${getStatusBgColor(ticket.status)};color:${getStatusColor(ticket.status)}`">
                                        {{ getStatusLabel(ticket.status) }}
                                    </span>
                                    <span class="sti-badge"
                                        :style="`background:${getPriorityBgColor(ticket.priority)};color:${getPriorityColor(ticket.priority)}`">
                                        {{ getPriorityLabel(ticket.priority) }}
                                    </span>
                                </div>
                            </div>
                            <p class="sti-ticket-desc">{{ ticket.description }}</p>
                            <div class="sti-ticket-meta">
                                <span class="sti-meta-item">
                                    <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"/>
                                    </svg>
                                    {{ ticket.ticket_number }}
                                </span>
                                <span class="sti-meta-item">
                                    <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                    </svg>
                                    {{ formatDate(ticket.created_at) }}
                                </span>
                                <span v-if="ticket.replies_count" class="sti-meta-item">
                                    <svg width="13" height="13" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/>
                                    </svg>
                                    {{ ticket.replies_count }} {{ t('replies') }}
                                </span>
                            </div>
                        </div>

                        <div class="sti-ticket-arrow">
                            <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                            </svg>
                        </div>
                    </div>
                </div>

                <!-- Empty state -->
                <div v-else class="sti-empty">
                    <div class="sti-empty-icon">
                        <svg width="40" height="40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/>
                        </svg>
                    </div>
                    <p class="sti-empty-text">{{ t('noSupportTicketsFound') }}</p>
                    <Link href="/user/support-tickets/create" class="sti-btn-primary">
                        {{ t('createYourFirstTicket') }}
                        <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                        </svg>
                    </Link>
                </div>
            </div>

        </div>
    </UserLayout>
</template>

<script setup>
import {ref, computed, onMounted} from 'vue';
import {usePage, Link, router} from '@inertiajs/vue3';
import UserLayout from "@/Layouts/UserLayout/UserLayout.vue";
import {useToast} from "@/composables/useToast.js";
import {useTranslation} from '@/composables/useTranslation';

const props = defineProps({
    tickets: {
        type: Array,
        default: () => [],
        validator: (value) => Array.isArray(value)
    },
    meta: {
        type: Object,
        default: () => ({
            total: 0,
            current_page: 1,
            per_page: 10,
            last_page: 1
        }),
        validator: (value) => value && typeof value === 'object'
    },
    filters: {
        type: Object,
        default: () => ({
            search: '',
            status: '',
            sort_field: 'created_at',
            sort_direction: 'desc'
        }),
        validator: (value) => value && typeof value === 'object'
    }
});

const {showToast} = useToast();
const {t} = useTranslation();
const page = usePage();

const searchQuery = ref(props.filters.search || '');
const selectedStatus = ref(props.filters.status || '');
const sortField = ref(props.filters.sort_field || 'created_at');
const sortDirection = ref(props.filters.sort_direction || 'desc');

const primaryColor = computed(() => page.props.primaryColor || '#86efac');

const hexToRgb = (hex) => {
    if (!hex || typeof hex !== 'string') {
        return {r: 134, g: 239, b: 172};
    }
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : {r: 134, g: 239, b: 172};
};

const rgbToHex = (r, g, b) => {
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

const adjustColor = (hex, amount) => {
    const rgb = hexToRgb(hex);
    const newR = Math.min(255, Math.max(0, rgb.r + amount));
    const newG = Math.min(255, Math.max(0, rgb.g + amount));
    const newB = Math.min(255, Math.max(0, rgb.b + amount));
    return rgbToHex(newR, newG, newB);
};

const themeColors = computed(() => ({
    primary: primaryColor.value,
    primaryDark: adjustColor(primaryColor.value, -20),
    primaryLight: adjustColor(primaryColor.value, 40),
    primaryRgb: `${hexToRgb(primaryColor.value).r}, ${hexToRgb(primaryColor.value).g}, ${hexToRgb(primaryColor.value).b}`
}));

const statusConfig = {
    open: {
        label: t('open') || 'Open',
        color: '#3b82f6',
        bgColor: 'rgba(59, 130, 246, 0.1)',
        icon: 'M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z'
    },
    pending: {
        label: t('pending') || 'Pending',
        color: '#f59e0b',
        bgColor: 'rgba(245, 158, 11, 0.1)',
        icon: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
    },
    resolved: {
        label: t('resolved') || 'Resolved',
        color: '#10b981',
        bgColor: 'rgba(16, 185, 129, 0.1)',
        icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
    },
    closed: {
        label: t('closed') || 'Closed',
        color: '#6b7280',
        bgColor: 'rgba(107, 114, 128, 0.1)',
        icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z'
    }
};

const priorityConfig = {
    low: {
        label: t('low') || 'Low',
        color: '#6b7280',
        bgColor: 'rgba(107, 114, 128, 0.1)'
    },
    medium: {
        label: t('medium') || 'Medium',
        color: '#3b82f6',
        bgColor: 'rgba(59, 130, 246, 0.1)'
    },
    high: {
        label: t('high') || 'High',
        color: '#f59e0b',
        bgColor: 'rgba(245, 158, 11, 0.1)'
    },
    urgent: {
        label: t('urgent') || 'Urgent',
        color: '#ef4444',
        bgColor: 'rgba(239, 68, 68, 0.1)'
    }
};

const ticketStats = computed(() => {
    const stats = {
        total: props.meta?.total || props.tickets.length,
        open: 0,
        pending: 0,
        resolved: 0,
        closed: 0
    };

    props.tickets.forEach(ticket => {
        if (stats[ticket.status] !== undefined) {
            stats[ticket.status]++;
        }
    });

    return stats;
});

const applyFilters = () => {
    router.get('/user/support-tickets', {
        search: searchQuery.value,
        status: selectedStatus.value,
        sort_field: sortField.value,
        sort_direction: sortDirection.value
    }, {
        preserveState: true,
        preserveScroll: true
    });
};

const clearFilters = () => {
    searchQuery.value = '';
    selectedStatus.value = '';
    sortField.value = 'created_at';
    sortDirection.value = 'desc';
    applyFilters();
};

const formatDate = (dateString) => {
    if (!dateString) return t('notAvailable') || 'N/A';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return t('invalidDate') || 'Invalid date';
        }
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch (error) {
        console.error('Date formatting error:', error);
        return t('invalidDate') || 'Invalid date';
    }
};

const getStatusIcon = (status) => {
    return statusConfig[status]?.icon || statusConfig.open.icon;
};

const getStatusLabel = (status) => {
    return statusConfig[status]?.label || status.charAt(0).toUpperCase() + status.slice(1);
};

const getStatusColor = (status) => {
    return statusConfig[status]?.color || statusConfig.open.color;
};

const getStatusBgColor = (status) => {
    return statusConfig[status]?.bgColor || statusConfig.open.bgColor;
};

const getPriorityLabel = (priority) => {
    return priorityConfig[priority]?.label || priority.charAt(0).toUpperCase() + priority.slice(1);
};

const getPriorityColor = (priority) => {
    return priorityConfig[priority]?.color || priorityConfig.medium.color;
};

const getPriorityBgColor = (priority) => {
    return priorityConfig[priority]?.bgColor || priorityConfig.medium.bgColor;
};

onMounted(() => {
    try {
        const flash = page.props.flash;
        if (flash?.success) showToast(flash.success, 'success');
        if (flash?.error) showToast(flash.error, 'error');
        if (flash?.warning) showToast(flash.warning, 'warning');
        if (flash?.info) showToast(flash.info, 'info');
    } catch (error) {
        console.error('Component mount error:', error);
    }
});
</script>

<style scoped>
/* ── Shared animation ── */
.anim-up { animation: slideUp 0.45s cubic-bezier(0.22,1,0.36,1) both; animation-delay: var(--d,0ms); }
@keyframes slideUp { from{opacity:0;transform:translateY(22px)} to{opacity:1;transform:none} }

/* ── Page wrapper ── */
.sti-wrap {
    max-width: 1280px;
    margin: 0 auto;
    padding: 14px 12px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: 12px;
    position: relative;
    z-index: 10;
    width: 100%;
}
@media(min-width:640px){ .sti-wrap{ padding: 18px 18px; gap: 14px; } }
@media(min-width:1024px){ .sti-wrap{ padding: 20px 24px; gap: 16px; } }

/* ── Welcome strip ── */
.sti-welcome {
    display: flex; align-items: center; justify-content: space-between;
    flex-wrap: wrap; gap: 12px;
    background: rgba(167,93,237,0.06);
    border: 1px solid rgba(167,93,237,0.16);
    border-radius: 13px; padding: 12px 16px;
}
.sti-welcome-left { display: flex; align-items: center; gap: 10px; }
.sti-welcome-icon {
    width: 40px; height: 40px; border-radius: 11px;
    background: linear-gradient(135deg,rgba(124,58,237,0.3),rgba(167,93,237,0.2));
    border: 1px solid rgba(167,93,237,0.28);
    display: flex; align-items: center; justify-content: center;
    color: #a75ded; flex-shrink: 0;
    box-shadow: 0 0 14px rgba(167,93,237,0.22);
}
.sti-welcome-eyebrow { font-size: 9px; font-weight: 700; color: rgba(167,93,237,0.7); text-transform: uppercase; letter-spacing: 0.1em; }
.sti-welcome-title { font-size: 15px; font-weight: 900; color: #fff; letter-spacing: -0.02em; }
.sti-welcome-chips { display: flex; flex-wrap: wrap; gap: 6px; }
.sti-chip {
    display: flex; flex-direction: column; align-items: center; gap: 2px;
    padding: 6px 12px; border-radius: 9px;
    background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.07);
    min-width: 58px;
}
.sti-chip-lbl { font-size: 8px; font-weight: 700; color: #4b5563; text-transform: uppercase; letter-spacing: 0.12em; }
.sti-chip-val { font-size: 13px; font-weight: 900; color: #fff; }
.sti-chip--purple .sti-chip-val { color: #c084fc; }
.sti-chip--blue   .sti-chip-val { color: #60a5fa; }
.sti-chip--green  .sti-chip-val { color: #4ade80; }

/* ── Stat cards ── */
.sti-stats { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; }
@media(min-width:768px){ .sti-stats{ grid-template-columns: repeat(4, 1fr); } }

.sti-stat {
    background: rgba(10,4,22,0.97);
    border: 1px solid rgba(var(--ac),0.22);
    border-radius: 14px; padding: 12px;
    position: relative; overflow: hidden;
    transition: transform 0.25s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.25s;
    cursor: default;
}
.sti-stat:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(var(--ac),0.18); }
.sti-stat-label { font-size: 10px; font-weight: 700; color: #4b5563; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 6px; }
.sti-stat-val { font-size: 22px; font-weight: 900; color: #fff; line-height: 1; margin-bottom: 4px; }
.sti-stat-sub { font-size: 10px; font-weight: 700; }
@media(min-width:640px){ .sti-stat{ padding: 14px; } .sti-stat-val{ font-size: 26px; } }

/* ── Card ── */
.sti-card {
    background: rgba(10,4,22,0.97);
    border: 1px solid rgba(167,93,237,0.13);
    border-radius: 16px; padding: 16px;
    position: relative; overflow: hidden;
    transition: border-color 0.25s;
}
.sti-card::before {
    content: ''; position: absolute; top: 0; left: 14%; right: 14%; height: 1px;
    background: linear-gradient(90deg,transparent,rgba(167,93,237,0.32),transparent);
}
.sti-card:hover { border-color: rgba(167,93,237,0.24); }

/* ── Filter row ── */
.sti-filter-row { display: flex; flex-wrap: wrap; gap: 10px; align-items: flex-end; }
.sti-filter-search { flex: 1; min-width: 200px; }
.sti-filter-status { width: 160px; }
@media(max-width:600px){ .sti-filter-status{ width: 100%; } }
.sti-filter-actions { display: flex; align-items: flex-end; gap: 6px; flex-shrink: 0; }
.sti-field-label { font-size: 10px; font-weight: 700; color: rgba(255,255,255,0.45); text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 6px; display: block; }
.sti-input {
    width: 100%; box-sizing: border-box; padding: 9px 12px;
    background: rgba(10,4,22,0.7); border: 1px solid rgba(167,93,237,0.2);
    border-radius: 9px; color: #fff; font-size: 13px; font-weight: 500;
    outline: none; transition: border-color 0.2s, box-shadow 0.2s;
}
.sti-input::placeholder { color: rgba(255,255,255,0.25); }
.sti-input:focus { border-color: rgba(167,93,237,0.55); box-shadow: 0 0 0 3px rgba(167,93,237,0.1); }
.sti-select { cursor: pointer; }
.sti-select option { background: #1a0a40; color: #fff; }

.sti-btn-icon {
    width: 36px; height: 36px; border-radius: 9px;
    background: rgba(167,93,237,0.1); border: 1px solid rgba(167,93,237,0.25);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer; color: #a75ded; transition: background 0.2s;
}
.sti-btn-icon:hover { background: rgba(167,93,237,0.2); }
.sti-btn-ghost {
    padding: 9px 14px; background: rgba(10,4,22,0.7);
    border: 1px solid rgba(167,93,237,0.15); border-radius: 9px;
    color: rgba(255,255,255,0.5); font-size: 12px; font-weight: 600;
    cursor: pointer; transition: color 0.2s, background 0.2s; white-space: nowrap;
}
.sti-btn-ghost:hover { color: #fff; background: rgba(167,93,237,0.08); }
.sti-btn-primary {
    display: inline-flex; align-items: center; gap: 5px;
    padding: 9px 16px;
    background: linear-gradient(135deg, #a75ded, #7c3aed);
    border: none; border-radius: 9px;
    color: #fff; font-size: 12px; font-weight: 700;
    cursor: pointer; text-decoration: none;
    transition: opacity 0.2s, transform 0.15s; white-space: nowrap;
}
.sti-btn-primary:hover { opacity: 0.9; transform: translateY(-1px); }
.sti-hide-xs { display: inline; }
.sti-show-xs { display: none; }
@media(max-width:400px){ .sti-hide-xs{ display:none; } .sti-show-xs{ display:inline; } }

/* ── List head ── */
.sti-list-head { margin-bottom: 14px; }
.sti-list-title { font-size: 13px; font-weight: 800; color: #fff; letter-spacing: -0.01em; }
.sti-list-sub { font-size: 10px; color: #374151; margin-top: 2px; }

/* ── Ticket items ── */
.sti-list { display: flex; flex-direction: column; gap: 8px; }
.sti-ticket-item {
    display: flex; align-items: flex-start; gap: 12px; padding: 12px;
    background: rgba(10,4,22,0.5); border: 1px solid rgba(167,93,237,0.1);
    border-radius: 10px; cursor: pointer; transition: background 0.2s, border-color 0.2s;
}
.sti-ticket-item:hover { background: rgba(167,93,237,0.06); border-color: rgba(167,93,237,0.25); }
.sti-ticket-icon { width: 38px; height: 38px; border-radius: 9px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.sti-ticket-body { flex: 1; min-width: 0; }
.sti-ticket-top { display: flex; align-items: flex-start; flex-wrap: wrap; gap: 6px; margin-bottom: 5px; }
.sti-ticket-subject { font-size: 13px; font-weight: 600; color: #fff; flex: 1; min-width: 0; }
.sti-ticket-badges { display: flex; gap: 5px; flex-wrap: wrap; flex-shrink: 0; }
.sti-badge { padding: 2px 7px; border-radius: 6px; font-size: 10px; font-weight: 700; }
.sti-ticket-desc {
    font-size: 11px; color: rgba(255,255,255,0.45); margin-bottom: 6px;
    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.sti-ticket-meta { display: flex; flex-wrap: wrap; gap: 10px; }
.sti-meta-item { display: flex; align-items: center; gap: 4px; font-size: 10px; color: rgba(255,255,255,0.3); }
.sti-ticket-arrow { color: rgba(255,255,255,0.2); flex-shrink: 0; margin-top: 2px; transition: color 0.2s; }
.sti-ticket-item:hover .sti-ticket-arrow { color: rgba(167,93,237,0.6); }

/* ── Empty state ── */
.sti-empty { display: flex; flex-direction: column; align-items: center; gap: 14px; padding: 40px 16px; text-align: center; }
.sti-empty-icon {
    width: 60px; height: 60px; border-radius: 50%;
    background: rgba(167,93,237,0.08); border: 1px solid rgba(167,93,237,0.18);
    display: flex; align-items: center; justify-content: center; color: rgba(167,93,237,0.4);
}
.sti-empty-text { font-size: 13px; color: rgba(255,255,255,0.4); }
</style>
